import { useState, useEffect } from "react";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  LayoutDashboard,
  Package,
  Tag,
  MessageSquare,
  FileText,
  LogOut,
  Plus,
  Edit,
  Trash2,
  Boxes,
  Eye,
  EyeOff,
  Leaf,
  FileDown,
  Database,
} from "lucide-react";
import ImageUploader from "@/components/ImageUploader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auth Context
const useAuth = () => {
  const [auth, setAuth] = useState(() => {
    const saved = sessionStorage.getItem("adminAuth");
    return saved ? JSON.parse(saved) : null;
  });

  const login = (credentials) => {
    sessionStorage.setItem("adminAuth", JSON.stringify(credentials));
    setAuth(credentials);
  };

  const logout = () => {
    sessionStorage.removeItem("adminAuth");
    setAuth(null);
  };

  const getAuthHeader = () => {
    if (!auth) return {};
    return {
      headers: {
        Authorization: `Basic ${btoa(`${auth.username}:${auth.password}`)}`,
      },
    };
  };

  return { auth, login, logout, getAuthHeader };
};

// Login Component
function AdminLogin({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await axios.get(`${API}/auth/verify`, {
        headers: { Authorization: `Basic ${btoa(`${username}:${password}`)}` },
      });
      onLogin({ username, password });
      toast.success("Connexion réussie");
    } catch (err) {
      setError("Identifiants incorrects");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4" data-testid="admin-login">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-primary/10 rounded-full p-3">
              <Leaf className="h-8 w-8 text-primary" />
            </div>
          </div>
          <CardTitle className="font-heading text-2xl">Administration</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="username">Identifiant</Label>
              <Input
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                data-testid="admin-username"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="admin-password"
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="admin-login-btn">
              {loading ? "Connexion..." : "Se connecter"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// Sidebar Navigation
function AdminSidebar({ onLogout }) {
  const location = useLocation();
  
  const navItems = [
    { path: "/admin", icon: LayoutDashboard, label: "Tableau de bord", exact: true },
    { path: "/admin/products", icon: Package, label: "Produits" },
    { path: "/admin/lots", icon: Boxes, label: "Lots" },
    { path: "/admin/promotions", icon: Tag, label: "Promotions" },
    { path: "/admin/contacts", icon: MessageSquare, label: "Demandes" },
    { path: "/admin/content", icon: FileText, label: "Contenus" },
  ];

  const isActive = (path, exact) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <aside className="admin-sidebar flex flex-col">
      <div className="p-4 border-b border-gray-800">
        <div className="flex items-center gap-2">
          <Leaf className="h-6 w-6 text-secondary" />
          <span className="font-heading text-lg font-semibold">Admin</span>
        </div>
      </div>
      
      <nav className="flex-1 py-4">
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`admin-nav-item ${isActive(item.path, item.exact) ? "active" : ""}`}
            data-testid={`admin-nav-${item.label.toLowerCase().replace(" ", "-")}`}
          >
            <item.icon className="h-5 w-5" />
            <span>{item.label}</span>
          </Link>
        ))}
      </nav>
      
      <div className="p-4 border-t border-gray-800">
        <button
          onClick={onLogout}
          className="admin-nav-item w-full text-red-400 hover:bg-red-900/20"
          data-testid="admin-logout-btn"
        >
          <LogOut className="h-5 w-5" />
          <span>Déconnexion</span>
        </button>
      </div>
    </aside>
  );
}

// Dashboard
function Dashboard({ getAuthHeader }) {
  const [stats, setStats] = useState({ products: 0, lots: 0, promotions: 0, contacts: 0 });
  const [downloadingCatalog, setDownloadingCatalog] = useState(false);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [products, lots, promotions, contacts] = await Promise.all([
          axios.get(`${API}/products`),
          axios.get(`${API}/lots`),
          axios.get(`${API}/promotions`),
          axios.get(`${API}/contact`, getAuthHeader()),
        ]);
        setStats({
          products: products.data.length,
          lots: lots.data.length,
          promotions: promotions.data.filter(p => p.active).length,
          contacts: contacts.data.filter(c => !c.read).length,
        });
      } catch (error) {
        console.error("Error fetching stats:", error);
      }
    };
    fetchStats();
  }, []);

  const downloadCatalog = async () => {
    setDownloadingCatalog(true);
    try {
      const response = await axios.get(`${API}/catalog/pdf`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `catalogue_jardin_viande_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Catalogue téléchargé !");
    } catch (error) {
      console.error("Error downloading catalog:", error);
      toast.error("Erreur lors du téléchargement du catalogue");
    } finally {
      setDownloadingCatalog(false);
    }
  };

  const importFromPreview = async () => {
    if (!window.confirm("⚠️ ATTENTION: Cette action va remplacer TOUTES les données actuelles (produits, contenus) par celles de la preview. Continuer ?")) {
      return;
    }
    
    setImporting(true);
    try {
      // Fetch data from preview
      const previewUrl = "https://jardin-viande.preview.emergentagent.com/api/database/export";
      toast.info("Téléchargement des données depuis la preview...");
      
      const exportResponse = await axios.get(previewUrl);
      const exportData = exportResponse.data;
      
      toast.info("Import en cours...");
      
      // Import using secret key endpoint (works without database auth)
      const importPayload = {
        secret: "JardinViande2024Import!",
        collections: exportData.collections
      };
      
      const importResponse = await axios.post(`${API}/database/import-with-secret`, importPayload);
      
      toast.success(`Import réussi ! ${importResponse.data.imported?.products || 0} produits importés.`);
      
      // Refresh stats
      window.location.reload();
    } catch (error) {
      console.error("Error importing:", error);
      toast.error("Erreur lors de l'import: " + (error.response?.data?.detail || error.message));
    } finally {
      setImporting(false);
    }
  };

  const statCards = [
    { label: "Produits", value: stats.products, icon: Package, color: "bg-blue-500" },
    { label: "Lots", value: stats.lots, icon: Boxes, color: "bg-green-500" },
    { label: "Promos actives", value: stats.promotions, icon: Tag, color: "bg-orange-500" },
    { label: "Demandes non lues", value: stats.contacts, icon: MessageSquare, color: "bg-purple-500" },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="font-heading text-2xl font-bold text-gray-900">Tableau de bord</h1>
        <div className="flex gap-2 flex-wrap">
          <Button 
            onClick={importFromPreview} 
            disabled={importing}
            variant="outline"
            className="border-orange-500 text-orange-600 hover:bg-orange-50"
            data-testid="import-from-preview-btn"
          >
            <Database className="h-4 w-4 mr-2" />
            {importing ? "Import en cours..." : "Importer depuis Preview"}
          </Button>
          <Button 
            onClick={downloadCatalog} 
            disabled={downloadingCatalog}
            className="bg-primary hover:bg-primary/90"
            data-testid="download-catalog-btn"
          >
            <FileDown className="h-4 w-4 mr-2" />
            {downloadingCatalog ? "Génération..." : "Télécharger le catalogue PDF"}
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, i) => (
          <Card key={i}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">{stat.label}</p>
                  <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                </div>
                <div className={`${stat.color} p-3 rounded-full text-white`}>
                  <stat.icon className="h-6 w-6" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// Products Management
function ProductsManager({ getAuthHeader, auth }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editProduct, setEditProduct] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [filterCategory, setFilterCategory] = useState("all");
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    image_url: "",
    category: "viandes",
    subcategory: "",
    price: "",
    price_per_kg: "",
    is_promotion: false,
    promotion_price: "",
  });

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  // Filter products by category
  const filteredProducts = filterCategory === "all" 
    ? products 
    : products.filter(p => p.category === filterCategory);

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      image_url: "",
      category: "viandes",
      subcategory: "",
      price: "",
      price_per_kg: "",
      is_promotion: false,
      promotion_price: "",
    });
    setEditProduct(null);
  };

  const handleEdit = (product) => {
    setEditProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      image_url: product.image_url,
      category: product.category,
      subcategory: product.subcategory || "",
      price: product.price || "",
      price_per_kg: product.price_per_kg || "",
      is_promotion: product.is_promotion || false,
      promotion_price: product.promotion_price || "",
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = { ...formData };
      if (!dataToSend.subcategory) delete dataToSend.subcategory;
      if (!dataToSend.price_per_kg) delete dataToSend.price_per_kg;
      if (editProduct) {
        await axios.put(`${API}/products/${editProduct.id}`, dataToSend, getAuthHeader());
        toast.success("Produit modifié");
      } else {
        await axios.post(`${API}/products`, dataToSend, getAuthHeader());
        toast.success("Produit ajouté");
      }
      fetchProducts();
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce produit ?")) return;
    try {
      await axios.delete(`${API}/products/${id}`, getAuthHeader());
      toast.success("Produit supprimé");
      fetchProducts();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const categoryLabels = {
    viandes: "Viandes",
    fruits_legumes: "Fruits & Légumes",
    cremerie: "Crèmerie",
    charcuterie: "Charcuterie",
    conserves: "Conserves",
    desserts: "Desserts",
  };

  const subcategoryLabels = {
    boeuf: "Bœuf",
    veau: "Veau",
    agneau: "Agneau",
    porc: "Porc",
    volailles: "Volailles",
    fruits: "Fruits",
    legumes: "Légumes",
    plats_cuisines: "Plats cuisinés",
  };

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl font-bold text-gray-900">Produits</h1>
        <div className="flex items-center gap-4">
          {/* Category Filter */}
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-48" data-testid="filter-category">
              <SelectValue placeholder="Filtrer par catégorie" />
            </SelectTrigger>
            <SelectContent container={null}>
              <SelectItem value="all">Toutes les catégories</SelectItem>
              <SelectItem value="viandes">Viandes</SelectItem>
              <SelectItem value="fruits_legumes">Fruits & Légumes</SelectItem>
              <SelectItem value="cremerie">Crèmerie</SelectItem>
              <SelectItem value="charcuterie">Charcuterie</SelectItem>
              <SelectItem value="conserves">Conserves</SelectItem>
              <SelectItem value="desserts">Desserts</SelectItem>
            </SelectContent>
          </Select>
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-product-btn">
                <Plus className="h-4 w-4 mr-2" /> Ajouter
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editProduct ? "Modifier le produit" : "Nouveau produit"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Nom</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="product-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Catégorie</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v, subcategory: "" })}>
                  <SelectTrigger data-testid="product-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="viandes">Viandes</SelectItem>
                    <SelectItem value="fruits_legumes">Fruits & Légumes</SelectItem>
                    <SelectItem value="cremerie">Crèmerie</SelectItem>
                    <SelectItem value="charcuterie">Charcuterie</SelectItem>
                    <SelectItem value="conserves">Conserves</SelectItem>
                    <SelectItem value="desserts">Desserts</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {formData.category === "viandes" && (
                <div className="space-y-2">
                  <Label>Sous-catégorie</Label>
                  <Select value={formData.subcategory} onValueChange={(v) => setFormData({ ...formData, subcategory: v })}>
                    <SelectTrigger data-testid="product-subcategory-select">
                      <SelectValue placeholder="Sélectionner..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="boeuf">Bœuf</SelectItem>
                      <SelectItem value="veau">Veau</SelectItem>
                      <SelectItem value="agneau">Agneau</SelectItem>
                      <SelectItem value="porc">Porc</SelectItem>
                      <SelectItem value="volailles">Volailles</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              {formData.category === "fruits_legumes" && (
                <div className="space-y-2">
                  <Label>Sous-catégorie</Label>
                  <Select value={formData.subcategory} onValueChange={(v) => setFormData({ ...formData, subcategory: v })}>
                    <SelectTrigger data-testid="product-subcategory-select">
                      <SelectValue placeholder="Sélectionner..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fruits">Fruits</SelectItem>
                      <SelectItem value="legumes">Légumes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              {formData.category === "conserves" && (
                <div className="space-y-2">
                  <Label>Sous-catégorie</Label>
                  <Select value={formData.subcategory} onValueChange={(v) => setFormData({ ...formData, subcategory: v })}>
                    <SelectTrigger data-testid="product-subcategory-select">
                      <SelectValue placeholder="Sélectionner..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="plats_cuisines">Plats cuisinés</SelectItem>
                      <SelectItem value="legumes">Légumes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  data-testid="product-description-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Image du produit</Label>
                <ImageUploader
                  value={formData.image_url}
                  onChange={(url) => setFormData({ ...formData, image_url: url })}
                  auth={auth}
                />
              </div>
              <div className="space-y-2">
                <Label>Prix</Label>
                <Input
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  placeholder="ex: 12,50€"
                  data-testid="product-price-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Prix au kilo (optionnel)</Label>
                <Input
                  value={formData.price_per_kg}
                  onChange={(e) => setFormData({ ...formData, price_per_kg: e.target.value })}
                  placeholder="ex: 40,50€/kg"
                  data-testid="product-price-per-kg-input"
                />
                <p className="text-xs text-gray-500">Affiché en petit sous le prix principal</p>
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={formData.is_promotion}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_promotion: checked })}
                  data-testid="product-promo-switch"
                />
                <Label>En promotion</Label>
              </div>
              {formData.is_promotion && (
                <div className="space-y-2">
                  <Label>Prix promotionnel</Label>
                  <Input
                    value={formData.promotion_price}
                    onChange={(e) => setFormData({ ...formData, promotion_price: e.target.value })}
                    placeholder="ex: 9,90€/kg"
                    data-testid="product-promo-price-input"
                  />
                </div>
              )}
              <DialogFooter>
                <DialogClose asChild>
                  <Button type="button" variant="outline">Annuler</Button>
                </DialogClose>
                <Button type="submit" data-testid="save-product-btn">Enregistrer</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Products count */}
      <div className="mb-4 text-sm text-gray-500">
        {filteredProducts.length} produit{filteredProducts.length > 1 ? 's' : ''} 
        {filterCategory !== "all" && ` dans ${categoryLabels[filterCategory]}`}
      </div>

      <div className="bg-white rounded-lg border overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Image</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Nom</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Catégorie</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Sous-cat.</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Prix</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Promo</th>
              <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y" key={`products-${filterCategory}`}>
            {filteredProducts.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                  Aucun produit dans cette catégorie
                </td>
              </tr>
            ) : (
              filteredProducts.map((product) => (
                <tr key={product.id} data-testid={`product-row-${product.id}`}>
                  <td className="px-4 py-3">
                    {product.image_url ? (
                      <img src={product.image_url} alt="" className="w-12 h-12 object-cover rounded" />
                    ) : (
                      <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center">
                        <Package className="h-6 w-6 text-gray-400" />
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 font-medium">{product.name}</td>
                  <td className="px-4 py-3 text-gray-500">{categoryLabels[product.category]}</td>
                  <td className="px-4 py-3 text-gray-500">{product.subcategory ? subcategoryLabels[product.subcategory] : "-"}</td>
                  <td className="px-4 py-3">{product.price || "-"}</td>
                  <td className="px-4 py-3">
                    {product.is_promotion ? (
                      <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded text-xs">Oui</span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="ghost" onClick={() => handleEdit(product)} data-testid={`edit-product-${product.id}`}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(product.id)} data-testid={`delete-product-${product.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Lots Management
function LotsManager({ getAuthHeader, auth }) {
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editLot, setEditLot] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    contents: "",
    image_url: "",
    price: "",
    is_promotion: false,
    promotion_price: "",
  });

  const fetchLots = async () => {
    try {
      const response = await axios.get(`${API}/lots`);
      setLots(response.data);
    } catch (error) {
      console.error("Error fetching lots:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLots();
  }, []);

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      contents: "",
      image_url: "",
      price: "",
      is_promotion: false,
      promotion_price: "",
    });
    setEditLot(null);
  };

  const handleEdit = (lot) => {
    setEditLot(lot);
    setFormData({
      name: lot.name,
      description: lot.description,
      contents: lot.contents,
      image_url: lot.image_url,
      price: lot.price || "",
      is_promotion: lot.is_promotion || false,
      promotion_price: lot.promotion_price || "",
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editLot) {
        await axios.put(`${API}/lots/${editLot.id}`, formData, getAuthHeader());
        toast.success("Lot modifié");
      } else {
        await axios.post(`${API}/lots`, formData, getAuthHeader());
        toast.success("Lot ajouté");
      }
      fetchLots();
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce lot ?")) return;
    try {
      await axios.delete(`${API}/lots/${id}`, getAuthHeader());
      toast.success("Lot supprimé");
      fetchLots();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  return (
    <div data-testid="admin-lots">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl font-bold text-gray-900">Lots</h1>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-lot-btn">
              <Plus className="h-4 w-4 mr-2" /> Ajouter
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editLot ? "Modifier le lot" : "Nouveau lot"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Nom</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="lot-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  data-testid="lot-description-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Contenu (aliments et poids)</Label>
                <Textarea
                  value={formData.contents}
                  onChange={(e) => setFormData({ ...formData, contents: e.target.value })}
                  required
                  rows={4}
                  placeholder="• Entrecôte 500g&#10;• Saucisses 1kg"
                  data-testid="lot-contents-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Image du lot</Label>
                <ImageUploader
                  value={formData.image_url}
                  onChange={(url) => setFormData({ ...formData, image_url: url })}
                  auth={auth}
                />
              </div>
              <div className="space-y-2">
                <Label>Prix</Label>
                <Input
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  placeholder="ex: 45,90€"
                  data-testid="lot-price-input"
                />
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={formData.is_promotion}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_promotion: checked })}
                  data-testid="lot-promo-switch"
                />
                <Label>En promotion</Label>
              </div>
              {formData.is_promotion && (
                <div className="space-y-2">
                  <Label>Prix promotionnel</Label>
                  <Input
                    value={formData.promotion_price}
                    onChange={(e) => setFormData({ ...formData, promotion_price: e.target.value })}
                    data-testid="lot-promo-price-input"
                  />
                </div>
              )}
              <DialogFooter>
                <DialogClose asChild>
                  <Button type="button" variant="outline">Annuler</Button>
                </DialogClose>
                <Button type="submit" data-testid="save-lot-btn">Enregistrer</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {lots.map((lot) => (
          <Card key={lot.id} data-testid={`lot-card-${lot.id}`}>
            <div className="aspect-video overflow-hidden">
              <img src={lot.image_url} alt={lot.name} className="w-full h-full object-cover" />
            </div>
            <CardContent className="p-4">
              <h3 className="font-semibold">{lot.name}</h3>
              <p className="text-sm text-gray-500 mt-1">{lot.description}</p>
              <p className="text-primary font-bold mt-2">{lot.price}</p>
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => handleEdit(lot)} data-testid={`edit-lot-${lot.id}`}>
                  <Edit className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="outline" className="text-red-500" onClick={() => handleDelete(lot.id)} data-testid={`delete-lot-${lot.id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// Promotions Management
function PromotionsManager({ getAuthHeader, auth }) {
  const [promotions, setPromotions] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editPromo, setEditPromo] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    image_url: "",
    discount: "",
    active: true,
  });

  const fetchPromotions = async () => {
    try {
      const response = await axios.get(`${API}/promotions`);
      setPromotions(response.data);
    } catch (error) {
      console.error("Error fetching promotions:", error);
    }
  };

  useEffect(() => {
    fetchPromotions();
  }, []);

  const resetForm = () => {
    setFormData({ title: "", description: "", image_url: "", discount: "", active: true });
    setEditPromo(null);
  };

  const handleEdit = (promo) => {
    setEditPromo(promo);
    setFormData({
      title: promo.title,
      description: promo.description,
      image_url: promo.image_url || "",
      discount: promo.discount || "",
      active: promo.active,
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editPromo) {
        await axios.put(`${API}/promotions/${editPromo.id}`, formData, getAuthHeader());
        toast.success("Promotion modifiée");
      } else {
        await axios.post(`${API}/promotions`, formData, getAuthHeader());
        toast.success("Promotion ajoutée");
      }
      fetchPromotions();
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const toggleActive = async (promo) => {
    try {
      await axios.put(`${API}/promotions/${promo.id}`, { active: !promo.active }, getAuthHeader());
      toast.success(promo.active ? "Promotion désactivée" : "Promotion activée");
      fetchPromotions();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette promotion ?")) return;
    try {
      await axios.delete(`${API}/promotions/${id}`, getAuthHeader());
      toast.success("Promotion supprimée");
      fetchPromotions();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  return (
    <div data-testid="admin-promotions">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl font-bold text-gray-900">Promotions</h1>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-promo-btn">
              <Plus className="h-4 w-4 mr-2" /> Ajouter
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editPromo ? "Modifier la promotion" : "Nouvelle promotion"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Titre</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  data-testid="promo-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  data-testid="promo-description-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Image de la promotion (optionnel)</Label>
                <ImageUploader
                  value={formData.image_url}
                  onChange={(url) => setFormData({ ...formData, image_url: url })}
                  auth={auth}
                />
              </div>
              <div className="space-y-2">
                <Label>Réduction (optionnel)</Label>
                <Input
                  value={formData.discount}
                  onChange={(e) => setFormData({ ...formData, discount: e.target.value })}
                  placeholder="ex: -20%"
                  data-testid="promo-discount-input"
                />
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={formData.active}
                  onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                  data-testid="promo-active-switch"
                />
                <Label>Active</Label>
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button type="button" variant="outline">Annuler</Button>
                </DialogClose>
                <Button type="submit" data-testid="save-promo-btn">Enregistrer</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-4">
        {promotions.map((promo) => (
          <Card key={promo.id} className={!promo.active ? "opacity-50" : ""} data-testid={`promo-card-${promo.id}`}>
            <CardContent className="p-4 flex items-center gap-4">
              {promo.image_url && (
                <img src={promo.image_url} alt="" className="w-20 h-20 object-cover rounded" />
              )}
              <div className="flex-1">
                <h3 className="font-semibold">{promo.title}</h3>
                <p className="text-sm text-gray-500">{promo.description}</p>
                {promo.discount && <span className="text-accent font-bold">{promo.discount}</span>}
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="ghost" onClick={() => toggleActive(promo)} data-testid={`toggle-promo-${promo.id}`}>
                  {promo.active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => handleEdit(promo)} data-testid={`edit-promo-${promo.id}`}>
                  <Edit className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(promo.id)} data-testid={`delete-promo-${promo.id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// Contacts Management
function ContactsManager({ getAuthHeader }) {
  const [contacts, setContacts] = useState([]);

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/contact`, getAuthHeader());
      setContacts(response.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch (error) {
      console.error("Error fetching contacts:", error);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  const markAsRead = async (id) => {
    try {
      await axios.put(`${API}/contact/${id}/read`, {}, getAuthHeader());
      fetchContacts();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette demande ?")) return;
    try {
      await axios.delete(`${API}/contact/${id}`, getAuthHeader());
      toast.success("Demande supprimée");
      fetchContacts();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const sourceLabels = {
    bouche_a_oreille: "Bouche à oreille",
    pub_commercants: "PUB commerçants",
    journaux: "Journaux",
    hasard: "Hasard",
  };

  return (
    <div data-testid="admin-contacts">
      <h1 className="font-heading text-2xl font-bold text-gray-900 mb-6">Demandes de contact</h1>
      
      <div className="space-y-4">
        {contacts.length === 0 ? (
          <p className="text-gray-500 text-center py-8">Aucune demande de contact</p>
        ) : (
          contacts.map((contact) => (
            <Card key={contact.id} className={contact.read ? "bg-gray-50" : "border-primary"} data-testid={`contact-card-${contact.id}`}>
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold">{contact.prenom} {contact.nom}</h3>
                      {!contact.read && (
                        <span className="bg-primary text-white text-xs px-2 py-0.5 rounded">Nouveau</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">{contact.email} • {contact.telephone}</p>
                    {contact.source && (
                      <p className="text-sm text-primary mt-1">
                        Source : {sourceLabels[contact.source] || contact.source}
                      </p>
                    )}
                    <p className="mt-2 text-gray-700">{contact.message}</p>
                    <p className="text-xs text-gray-400 mt-2">
                      {new Date(contact.created_at).toLocaleString("fr-FR")}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {!contact.read && (
                      <Button size="sm" variant="outline" onClick={() => markAsRead(contact.id)} data-testid={`mark-read-${contact.id}`}>
                        Marquer lu
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(contact.id)} data-testid={`delete-contact-${contact.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}

// Content Management
function ContentManager({ getAuthHeader }) {
  const [contents, setContents] = useState([]);
  const [editContent, setEditContent] = useState(null);

  const contentLabels = {
    hero_title: "Titre principal (Hero)",
    hero_subtitle: "Sous-titre principal",
    delivery_zone: "Zone de livraison",
    no_promo_message: "Message aucune promotion",
    about_text: "Texte À propos",
    contact_phone: "Téléphone",
    contact_email: "Email",
    contact_address: "Adresse",
  };

  const fetchContents = async () => {
    try {
      const response = await axios.get(`${API}/content`);
      setContents(response.data);
    } catch (error) {
      console.error("Error fetching contents:", error);
    }
  };

  useEffect(() => {
    fetchContents();
  }, []);

  const handleSave = async (key, value) => {
    try {
      await axios.put(`${API}/content/${key}`, { value }, getAuthHeader());
      toast.success("Contenu mis à jour");
      setEditContent(null);
      fetchContents();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  return (
    <div data-testid="admin-content">
      <h1 className="font-heading text-2xl font-bold text-gray-900 mb-6">Contenus du site</h1>
      
      <div className="space-y-4">
        {contents.map((content) => (
          <Card key={content.key} data-testid={`content-card-${content.key}`}>
            <CardContent className="p-4">
              {editContent === content.key ? (
                <div className="space-y-3">
                  <Label>{contentLabels[content.key] || content.key}</Label>
                  <Textarea
                    defaultValue={content.value}
                    rows={3}
                    id={`edit-${content.key}`}
                    data-testid={`edit-content-${content.key}`}
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => {
                        const textarea = document.getElementById(`edit-${content.key}`);
                        handleSave(content.key, textarea.value);
                      }}
                      data-testid={`save-content-${content.key}`}
                    >
                      Enregistrer
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditContent(null)}>
                      Annuler
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-medium text-gray-900">{contentLabels[content.key] || content.key}</h3>
                    <p className="text-gray-600 mt-1">{content.value}</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => setEditContent(content.key)} data-testid={`edit-btn-${content.key}`}>
                    <Edit className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// Main Admin Component
export default function Admin() {
  const { auth, login, logout, getAuthHeader } = useAuth();

  if (!auth) {
    return <AdminLogin onLogin={login} />;
  }

  return (
    <div className="flex min-h-screen bg-gray-100">
      <AdminSidebar onLogout={logout} />
      <main className="flex-1 p-6 md:p-8 overflow-auto">
        <Routes>
          <Route index element={<Dashboard getAuthHeader={getAuthHeader} />} />
          <Route path="products" element={<ProductsManager getAuthHeader={getAuthHeader} auth={auth} />} />
          <Route path="lots" element={<LotsManager getAuthHeader={getAuthHeader} auth={auth} />} />
          <Route path="promotions" element={<PromotionsManager getAuthHeader={getAuthHeader} auth={auth} />} />
          <Route path="contacts" element={<ContactsManager getAuthHeader={getAuthHeader} />} />
          <Route path="content" element={<ContentManager getAuthHeader={getAuthHeader} />} />
        </Routes>
      </main>
    </div>
  );
}
